// @catalog F.006
// Helpers de formato del dashboard /ventas — etiquetas de meses, defaultPeriod,
// subtítulo dinámico del PageHead y el mapeo de tiendas a RankingItem. Extraídos
// de DashboardVentas.tsx para respetar P7 (≤500 líneas/archivo); presentación
// pura sin estado de React.
import AnalyticsService, {
  type MonthlyMetrics,
  type StoreMetrics,
} from "../../services/analytics.service";
import { monthNameToIndex } from "./helpers";
import { type RankingItem } from "../analytics/RankingBars";
import {
  type PeriodKey,
  type PeriodValue,
} from "../analytics/PeriodSelector";

export const MONTH_LABELS_FULL = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Abreviaturas para la etiqueta de rango del subtítulo (ej. "ene–may 2026").
export const MONTH_ABBREV = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

// Etiqueta corta del preset para el subtítulo multi-mes (ej. "YTD (ene–may 2026)").
export const PERIOD_LABELS: Record<PeriodKey, string> = {
  mtd: "MTD",
  "1m": "1M",
  "3m": "3M",
  ytd: "YTD",
  "12m": "12M",
  "24m": "24M",
};

// Etiqueta legible de un rango de meses (ej. "ene–may 2026" o cross-year
// "dic 2025 – feb 2026"). Usada por el banner para explicitar el período.
export function formatMonthRange(
  firstIdx: number,
  firstYear: number,
  lastIdx: number,
  lastYear: number,
): string {
  const f = MONTH_ABBREV[firstIdx] ?? "?";
  const l = MONTH_ABBREV[lastIdx] ?? "?";
  return firstYear === lastYear
    ? `${f}–${l} ${lastYear}`
    : `${f} ${firstYear} – ${l} ${lastYear}`;
}

export function isoToMonthKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}`;
}

export function clampToCurrentMonth(monthKey: string | null): string | null {
  if (!monthKey) return null;
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return monthKey > currentKey ? currentKey : monthKey;
}

// /ventas arranca SIEMPRE en el mes en curso (MTD = primer día del mes actual a
// hoy). El usuario puede cambiar el período durante su visita, pero al reabrir el
// módulo vuelve a mostrar el mes en curso por default.
export function defaultPeriod(): PeriodKey {
  return "mtd";
}

// Subtítulo dinámico del PageHead: refleja el período seleccionado y su agregado.
// Un mes → "Mayo 2026 · $Y [· de $Z proyectado · día N de M]"; varios meses →
// "<preset> (ene–may 2026) · $X total" (total del período).
export function buildVentasSubtitle(args: {
  current: MonthlyMetrics | null;
  sortedMonths: MonthlyMetrics[];
  currentLabel: string;
  period: PeriodValue;
  summaryLast3Months: number | null | undefined;
}): string {
  const { current, sortedMonths, currentLabel, period, summaryLast3Months } =
    args;
  const periodTotal =
    summaryLast3Months != null
      ? summaryLast3Months
      : sortedMonths.reduce((sum, m) => sum + m.salesAmount, 0);
  const subtitleParts: string[] = [];
  if (!current) {
    subtitleParts.push("Sin datos");
  } else if (sortedMonths.length <= 1) {
    subtitleParts.push(currentLabel);
    subtitleParts.push(AnalyticsService.formatCurrency(current.salesAmount));
    if (
      current.projectedSales !== undefined &&
      current.projectedSales !== current.salesAmount
    ) {
      subtitleParts.push(
        `de ${AnalyticsService.formatCurrency(current.projectedSales)} proyectado`,
      );
    }
    if (
      current.isCurrentMonth &&
      current.daysElapsed !== undefined &&
      current.daysInMonth !== undefined
    ) {
      subtitleParts.push(`día ${current.daysElapsed} de ${current.daysInMonth}`);
    }
  } else {
    const first = sortedMonths[0]!;
    const firstAbbr = MONTH_ABBREV[monthNameToIndex(first.month)] ?? first.month;
    const lastAbbr =
      MONTH_ABBREV[monthNameToIndex(current.month)] ?? current.month;
    const rangeLabel =
      first.year === current.year
        ? `${firstAbbr}–${lastAbbr} ${current.year}`
        : `${firstAbbr} ${first.year} – ${lastAbbr} ${current.year}`;
    const presetLabel = period !== "custom" ? PERIOD_LABELS[period] : null;
    subtitleParts.push(
      presetLabel ? `${presetLabel} (${rangeLabel})` : rangeLabel,
    );
    subtitleParts.push(`${AnalyticsService.formatCurrency(periodTotal)} total`);
  }
  return subtitleParts.join(" · ");
}

// Top-5 tiendas de un canal mapeadas a RankingItem para los RankingBars del
// Resumen. `threshold`/`tagLabel` parametrizan el tag de alerta por canal
// (POS: caída < -10% → "Atención"; SO: caída < -15% → "Bajo perf.").
export function toRankingItems(
  stores: StoreMetrics[],
  opts: { threshold: number; tagLabel: string },
): RankingItem<StoreMetrics>[] {
  return stores.slice(0, 5).map((s) => ({
    id: s.storeName,
    name: s.storeName,
    value: s.totalSales,
    delta: s.growthPercentage ?? null,
    tag:
      s.growthPercentage !== undefined &&
      s.growthPercentage !== null &&
      s.growthPercentage < opts.threshold
        ? opts.tagLabel
        : null,
    tagStyle: "alert" as const,
    raw: s,
  }));
}
